import {
  EnrichedHumanDocument,
  EnrichedMatterContact,
  EnrichedOrganizationDocument,
} from './aggregateTypes';
import {
  ContactRole,
  HumanData,
  HumanDocument,
  OrganizationData,
  OrganizationDocument,
} from '@shared/types';
import { FirmDatabase } from './firmDatabase';

export type EnrichedMatterContactsByRoleAndType = {
  client: {
    humans: EnrichedHumanDocument[];
    organizations: EnrichedOrganizationDocument[];
  };
  adverseParty: {
    humans: EnrichedHumanDocument[];
    organizations: EnrichedOrganizationDocument[];
  };
};

export const getEnrichedMatterContactsByRoleAndType = async (
  matterId: string,
  db: FirmDatabase,
): Promise<EnrichedMatterContactsByRoleAndType> => {
  const ungroupedContacts = await getEnrichedMatterContacts(matterId, db);

  const contactsByRole: EnrichedMatterContactsByRoleAndType = {
    client: { humans: [], organizations: [] },
    adverseParty: { humans: [], organizations: [] },
  };

  // Group each contact by role and type.
  for (const contact of ungroupedContacts) {
    if (contact.type === 'human') {
      contactsByRole[contact.role].humans.push(contact);
    } else {
      contactsByRole[contact.role].organizations.push(contact);
    }
  }

  // Sort each list of humans and organizations.
  for (const role in contactsByRole) {
    contactsByRole[role as ContactRole].humans.sort(
      compareHumansByPhoneAndLastName,
    );
    contactsByRole[role as ContactRole].organizations.sort((a, b) =>
      a.companyName.localeCompare(b.companyName),
    );
  }
  return contactsByRole;
};

export const getEnrichedMatterContacts = async (
  matterId: string,
  db: FirmDatabase,
): Promise<EnrichedMatterContact[]> => {
  const matterSnapshot = await db.getMatter(matterId);
  const humansAndOrganizations = matterSnapshot.data()?.humansAndOrganizations;
  if (!humansAndOrganizations) {
    return [];
  }

  return await Promise.all(
    humansAndOrganizations.map(async (humanOrOrganization) => {
      if (humanOrOrganization.type === 'human') {
        const snapshot = await db.getHuman(humanOrOrganization.id);
        return {
          ...(snapshot.data() as HumanData),
          id: snapshot.id,
          role: humanOrOrganization.role,
          type: 'human',
        };
      } else {
        const snapshot = await db.getOrganization(humanOrOrganization.id);
        return {
          ...(snapshot.data() as OrganizationData),
          id: snapshot.id,
          role: humanOrOrganization.role ?? 'client',
          type: 'organization',
        };
      }
    }),
  );
};

const compareHumansByPhoneAndLastName = (
  a: HumanDocument,
  b: HumanDocument,
) => {
  const aPhone = a.phoneNumbers?.find(
    (phone) => phone.id === a.primaryPhoneNumberId,
  )?.value;
  const bPhone = b.phoneNumbers?.find(
    (phone) => phone.id === b.primaryPhoneNumberId,
  )?.value;
  if (aPhone === bPhone) {
    return (a.lastName || '').localeCompare(b.lastName || '');
  }
  return (aPhone || '').localeCompare(bPhone || '');
};
